import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './CostAnalysis.css';
import { Conversation, ImplicitAttemptRecord, AttemptDetails } from '../types';
import { apiClient, PLAYGROUND } from '../services/apiClient';

// Base path for client-side routing (for deployments under subpaths)
const BASE_PATH = (() => {
    const raw = (process.env as any).PUBLIC_URL || '/';
    try {
        const u = new URL(raw, window.location.origin);
        return u.pathname.replace(/\/$/, '');
    } catch {
        return raw.replace(/\/$/, '');
    }
})();

type CostAnalysisProps = {
    environment?: string;
};

type DailyCostPoint = {
    key: string;
    date: Date;
    totalCost: number;
};

const CostAnalysis: React.FC<CostAnalysisProps> = ({ environment }) => {
    const HIGH_COST_THRESHOLD = 1;
    const [allAttemptIds, setAllAttemptIds] = useState<string[]>([]);
    const [sessions, setSessions] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const prevEnvironmentRef = useRef<string | undefined>(undefined);
    const [loadingPage, setLoadingPage] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Server now computes costs and caches them. Keep placeholders unused.
    const [dateFilter, setDateFilter] = useState('Last 14 Days');
    const [customStartDate, setCustomStartDate] = useState('');
    const [customEndDate, setCustomEndDate] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [sessionsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [aggregates, setAggregates] = useState<any>({});
    const [dailyCosts, setDailyCosts] = useState<Array<{ date: string; totalCost: number }>>([]);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [loadingMessage, setLoadingMessage] = useState('Initializing...');
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [refreshing, setRefreshing] = useState(false);
    const [tableFilter, setTableFilter] = useState<'all' | 'highCost' | 'recent'>('all');
    // Removed activeUserIds state - was causing expensive API calls on every page load
    const [attemptTypeFilter, setAttemptTypeFilter] = useState<'all' | 'student' | 'homework' | 'mocktest'>('all');
    // Toggle between canvas attempts and scan-and-grade homeworks. Persisted in
    // the URL so deep-links work and a page refresh stays on the same view.
    const [dataSource, setDataSource] = useState<'canvas' | 'scangrade'>(() => {
        const fromUrl = new URLSearchParams(window.location.search).get('source');
        return fromUrl === 'scangrade' ? 'scangrade' : 'canvas';
    });
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (dataSource === 'scangrade') params.set('source', 'scangrade');
        else params.delete('source');
        const q = params.toString();
        const newUrl = `${window.location.pathname}${q ? `?${q}` : ''}${window.location.hash}`;
        window.history.replaceState(null, '', newUrl);
    }, [dataSource]);
    const [orgFilter, setOrgFilter] = useState<string>('all');
    const [allOrgs, setAllOrgs] = useState<Array<{ _id: string; title: string }>>([]);
    const [orgSearchQuery, setOrgSearchQuery] = useState('');
    const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);

    // Question-level costs (fetched from separate API endpoint)
    const [questionLevelCosts, setQuestionLevelCosts] = useState({
        questionParsing: 0,
        questionValidation: 0,
        questionDifficulty: 0,
        conceptTagging: 0,
        curriculumTagging: 0,
        totalQuestions: 0
    });

    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const normalizeAttemptId = useCallback((session: any): string => String(session?.attemptId || session?._id || session?.socketId || '').trim(), []);
    const normalizeSocketId = useCallback((session: any): string => String(session?.socketId || '').trim(), []);
    const resolveAttemptDetails = useCallback((session: any): AttemptDetails | null => (session && session.attemptDetails) ? session.attemptDetails : null, []);

    const getSessionStartTimestampValue = useCallback((session: any): number => {
        if (!session) return 0;
        const ts = session.sessionStartTimestamp;
        if (typeof ts === 'number' && !Number.isNaN(ts)) return ts;
        if (typeof ts === 'string' && ts !== '') {
            const numericTs = Number(ts);
            if (!Number.isNaN(numericTs)) return numericTs;
            const parsedTs = new Date(ts).getTime();
            if (!Number.isNaN(parsedTs)) return parsedTs;
        }
        if (session.firstMessageTime) {
            const firstTs = typeof session.firstMessageTime === 'number'
                ? session.firstMessageTime
                : new Date(session.firstMessageTime).getTime();
            if (!Number.isNaN(firstTs)) return firstTs;
        }
        if (session.startTime) {
            const parsedTs = new Date(session.startTime).getTime();
            if (!Number.isNaN(parsedTs)) return parsedTs;
        }
        const endTs = session.sessionEndTimestamp;
        if (typeof endTs === 'number' && !Number.isNaN(endTs)) return endTs;
        if (typeof endTs === 'string' && endTs !== '') {
            const numericEndTs = Number(endTs);
            if (!Number.isNaN(numericEndTs)) return numericEndTs;
            const parsedEndTs = new Date(endTs).getTime();
            if (!Number.isNaN(parsedEndTs)) return parsedEndTs;
        }
        return 0;
    }, []);

    // Tokenizer no longer used on client – server computes costs

    const formatCost = (cost: number) => `$${cost.toFixed(2)}`;

    // Date label formatter for chart
    const dateLabelFormatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });

    // Format service names from camelCase to readable format
    const formatServiceName = (serviceName: string): string => {
        const nameMap: Record<string, string> = {
            'realtime': 'OpenAI Realtime',
            'geminiLive': 'Gemini Live',
            'supervisor': 'Supervisor',
            'intentRouter': 'Intent Router',
            'latex': 'LaTeX',
            'implicitChecker': 'Implicit Checker',
            'fermiAnalysis': 'Fermi Analysis',
            'questionParsing': 'Question Parsing',
            'questionValidation': 'Question Validation',
            'conceptTagging': 'Concept Tagging',
            'curriculumTagging': 'Curriculum Tagging',
            'questionDifficulty': 'Question Difficulty',
            'mockTestAnalysis': 'Mock Test Analysis',
            'overallQuestionAnalysis': 'Overall Question Analysis',
            'overallStudentAnalysis': 'Overall Student Analysis'
        };
        return nameMap[serviceName] || serviceName;
    };

    // Removed activeUserIdSet - no longer needed

    // Date filtering utility functions
    const getDateRangeFromFilter = useCallback(() => {
        const now = new Date();
        let startDate: Date;
        let endDate = new Date(now);

        switch (dateFilter) {
            case 'Last 24 Hours':
                startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
                break;
            case 'Last 7 Days':
                startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                break;
            case 'Last 14 Days':
                startDate = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
                break;
            case 'Last 30 Days':
                startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                break;
            case 'All Time':
                startDate = new Date(0); // Beginning of time
                endDate = new Date(); // Current time
                break;
            case 'Custom Range':
                if (customStartDate && customEndDate) {
                    startDate = new Date(customStartDate);
                    endDate = new Date(customEndDate + 'T23:59:59'); // End of day
                } else {
                    // If custom dates not set, default to all time
                    startDate = new Date(0);
                }
                break;
            default:
                startDate = new Date(0); // Default to all time
        }

        return { startDate, endDate };
    }, [dateFilter, customStartDate, customEndDate]);

    // Helper function to format start time as readable date/time
    const formatStartTime = useCallback((session: any): string => {
        if (!session) return 'Unknown';

        const startTime = session.sessionStartTimestamp || session.startTime;
        if (!startTime) return 'Unknown';

        // Convert to number if it's a string
        const startMs = typeof startTime === 'number' ? startTime : new Date(startTime).getTime();
        if (isNaN(startMs)) return 'Unknown';

        const date = new Date(startMs);
        return date.toLocaleString();
    }, []);

    const fetchSessionDetails = useCallback(async (attemptId: string) => {
        try {
            const payload = await apiClient.get<any>(`/api/conversations/${attemptId}`, PLAYGROUND);
            const conversationData: Conversation = Array.isArray(payload?.conversation)
                ? payload.conversation
                : Array.isArray(payload)    
                    ? payload
                    : [];
            const implicitAttempts: ImplicitAttemptRecord[] = Array.isArray(payload?.implicitAttempts)
                ? payload.implicitAttempts
                : [];

            // Safety check for conversation data
            if (!conversationData || !Array.isArray(conversationData)) {
                console.warn(`Invalid conversation data for ${attemptId}`);
                return { attemptId, error: true };
            }

            // Costs are provided by summaries; keep placeholder for details view
            const costDetails = { totalCost: 0, realtime: { totalCost: 0 }, tutor: { totalCost: 0 }, implicitChecker: { totalCost: 0 } };

            // Get session timing information
            const firstMessage = conversationData.find(msg => msg && (msg.timestamp || msg.createdAt));
            const lastMessage = [...conversationData].reverse().find(msg => msg && (msg.timestamp || msg.createdAt));

            const getTimestamp = (msg: any) => {
                if (msg.createdAt) {
                    return typeof msg.createdAt === 'number' ? msg.createdAt : new Date(msg.createdAt).getTime();
                }
                if (msg.timestamp) {
                    return typeof msg.timestamp === 'number' ? msg.timestamp : new Date(msg.timestamp).getTime();
                }
                return null;
            };

            const sessionStartTime = firstMessage ? getTimestamp(firstMessage) : null;
            const sessionEndTime = lastMessage ? getTimestamp(lastMessage) : null;

            const socketIdFromData = payload?.socketId
                || conversationData.find(msg => msg?.socketId)?.socketId
                || implicitAttempts.find(entry => entry?.socket_id)?.socket_id;
            const attemptIdFromData = payload?.attemptId
                || conversationData.find(msg => msg?.attemptId)?.attemptId
                || payload?._id
                || attemptId;
            const attemptDetails: AttemptDetails | null = payload?.attempt || null;
            const attemptUserIdFromPayload = payload?.attemptUserId ? String(payload.attemptUserId) : undefined;
            const statusFromPayload = payload?.status;
            const resolvedStatus = statusFromPayload || (attemptDetails ? (attemptDetails.isValid ? 'COMPLETED' : 'IN_PROGRESS') : 'IN_PROGRESS');

            return {
                attemptId: String(attemptIdFromData || attemptId),
                socketId: socketIdFromData || null,
                conversation: conversationData,
                implicitAttempts,
                costs: costDetails,
                attemptDetails,
                status: resolvedStatus,
                attemptUserId: attemptUserIdFromPayload,
                startTime: sessionStartTime
                    ? new Date(sessionStartTime).toLocaleString()
                    : 'Unknown',
                endTime: sessionEndTime
                    ? new Date(sessionEndTime).toLocaleString()
                    : 'Unknown',
                sessionStartTimestamp: sessionStartTime,
                sessionEndTimestamp: sessionEndTime,
                messageCount: conversationData.length,
                firstMessageTime: sessionStartTime,
                lastMessageTime: sessionEndTime,
                implicitAttemptCount: implicitAttempts.length,
            };
        } catch (err) {
            console.warn(`Failed to fetch details for ${attemptId}:`, err);
            return { attemptId, error: true };
        }
    }, []);
    
    // Removed fetchActiveUsers - was causing expensive query on every page load (fetching all users + their session counts)

    const fetchOrganizations = useCallback(async () => {
        try {
            console.log('[CostAnalysis] Fetching organizations...');
            const response = await apiClient.get<any>('/api/orgs?limit=200', PLAYGROUND);
            console.log('[CostAnalysis] Received organizations:', response);
            setAllOrgs(Array.isArray(response) ? response : response?.orgs || []);
        } catch (err: any) {
            console.error('Error fetching organizations:', err);
        }
    }, []);

    const fetchQuestionCosts = useCallback(async () => {
        try {
            console.log('[CostAnalysis] Fetching question-level costs...');
            const data = await apiClient.get<any>('/api/costs/questions', PLAYGROUND);
            console.log('[CostAnalysis] Received question costs:', data);

            setQuestionLevelCosts({
                questionParsing: data.questionParsing?.totalCost || 0,
                questionValidation: data.questionValidation?.totalCost || 0,
                questionDifficulty: data.questionDifficulty?.totalCost || 0,
                conceptTagging: data.conceptTagging?.totalCost || 0,
                curriculumTagging: data.curriculumTagging?.totalCost || 0,
                totalQuestions: data.summary?.totalQuestions || 0
            });
        } catch (err: any) {
            console.error('Error fetching question costs:', err);
            // Don't throw - just log the error, dashboard can still show attempt costs
        }
    }, []);

    const fetchPage = useCallback(async (pageNum: number, isInitialLoad = false) => {
        if (isInitialLoad) {
            setLoading(true);
            setLoadingMessage('Fetching session summaries...');
            setLoadingProgress(10);
        } else {
            setLoadingPage(true);
        }
        setError(null);
        try {
            const params = new URLSearchParams();
            params.set('page', String(pageNum));
            params.set('limit', String(sessionsPerPage));

            // Date filter
            if (dateFilter !== 'All Time') {
                const { startDate, endDate } = getDateRangeFromFilter();
                params.set('dateFrom', String(startDate.getTime()));
                params.set('dateTo', String(endDate.getTime()));
            }

            // Type filter
            if (attemptTypeFilter !== 'all') {
                const typeMap: Record<string, string> = { student: 'Student Attempts', homework: 'Homework Attempts', mocktest: 'MockTest Attempts' };
                params.set('type', typeMap[attemptTypeFilter]);
            }

            if (orgFilter !== 'all') params.set('userOrg', orgFilter);
            if (debouncedSearchTerm.trim()) params.set('search', debouncedSearchTerm.trim());

            // Sort
            if (tableFilter === 'highCost') {
                params.set('sortBy', 'costs.totalCost');
                params.set('sortOrder', 'desc');
            }

            console.log('[CostAnalysis] fetchPage', pageNum, params.toString(), 'source=', dataSource);
            if (dataSource === 'scangrade') {
                // Scan-grade: list HW parents with rolled-up cost columns. We
                // reuse the canvas table by mapping each parent into a
                // session-shaped object whose attemptId is the scanGradeHomeworkId.
                const sgParams = new URLSearchParams();
                sgParams.set('page', String(pageNum));
                sgParams.set('limit', String(sessionsPerPage));
                if (orgFilter !== 'all') sgParams.set('userOrg', orgFilter);
                if (debouncedSearchTerm.trim()) sgParams.set('search', debouncedSearchTerm.trim());
                if (dateFilter !== 'All Time') {
                    const { startDate, endDate } = getDateRangeFromFilter();
                    sgParams.set('dateFrom', String(startDate.getTime()));
                    sgParams.set('dateTo', String(endDate.getTime()));
                }
                const sgResponse = await apiClient.get<any>(`/api/scan-grade/homework?${sgParams}`, PLAYGROUND);
                const mapped = (sgResponse.data || []).map((p: any) => {
                    // Orphan = single scan-grade attempt with no homework parent.
                    // The server tells us via `isOrphan`; we route those to the
                    // per-question viewer instead of the homework viewer.
                    const isOrphan = Boolean(p.isOrphan);
                    return {
                        attemptId: isOrphan ? p.scanGradeId : (p.scanGradeHomeworkId || p.scanGradeId),
                        scanGradeId: p.scanGradeId,
                        scanGradeHomeworkId: p.scanGradeHomeworkId,
                        socketId: '',
                        isScanGradeHomework: !isOrphan,
                        isScanGradeQuestion: isOrphan,
                        sessionStartTimestamp: p.sessionStartTimestamp,
                        startTime: p.startTime,
                        numQuestions: p.numQuestions ?? p.rollup?.childCount ?? (isOrphan ? 1 : 0),
                        rollup: p.rollup || {},
                        costs: {
                            totalCost: p.rollup?.totalCost || 0,
                            scanGradeSegmentation: { totalCost: p.rollup?.segmentationCost || 0 },
                            scanGradeGrading: { totalCost: p.rollup?.gradingCost || 0 },
                        },
                    };
                });
                setSessions(mapped);
                setTotalCount(sgResponse.pagination?.totalCount || mapped.length);
                setAggregates({});
                setDailyCosts([]);
                setAllAttemptIds(mapped.map((s: any) => s.attemptId).filter(Boolean));
                setCurrentPage(pageNum);
            } else {
                const response = await apiClient.get<any>(`/api/sessions/summary?${params}`, PLAYGROUND);
                setSessions(response.data);
                setTotalCount(response.pagination.totalCount);
                setAggregates(response.aggregates);
                setDailyCosts(response.dailyCosts);
                setAllAttemptIds(Array.isArray(response.data) ? response.data.map((s: any) => String(s?.attemptId || s?.socketId || '')).filter((id: string) => id) : []);
                setCurrentPage(pageNum);
            }

            if (isInitialLoad) {
                setLoadingProgress(90);
                setLoadingMessage('Complete!');
                setLoadingProgress(100);
            }
        } catch (err: any) {
            console.error('Error fetching session summaries:', err);
            setError(err.name === 'AbortError' ? 'Fetching session summaries timed out.' : err.message);
            if (isInitialLoad) {
                setLoadingMessage(err.name === 'AbortError' ? 'Request timed out after 30s' : 'Error fetching summaries');
            }
        } finally {
            if (isInitialLoad) setLoading(false);
            setLoadingPage(false);
        }
    }, [sessionsPerPage, dateFilter, getDateRangeFromFilter, attemptTypeFilter, orgFilter, debouncedSearchTerm, tableFilter, dataSource]);

    const fetchSessionSummaries = useCallback(async () => {
        await fetchPage(1, true);
        await fetchQuestionCosts();
        await fetchOrganizations();
    }, [fetchPage, fetchQuestionCosts, fetchOrganizations]);

    const refreshSessionSummaries = useCallback(async () => {
        setRefreshing(true);
        setLoadingMessage('Starting background refresh...');
        setLoadingProgress(0);

        try {
            // Step 1: Start question cost computation with progress tracking
            try {
                setLoadingMessage('Starting question cost computation...');
                setLoadingProgress(0);
                const { jobId: questionJobId } = await apiClient.post<{ jobId: string }>(
                    '/api/costs/questions/compute',
                    PLAYGROUND
                );

                if (questionJobId) {
                    console.log(`✅ Started question cost computation: ${questionJobId}`);

                    // Poll for question cost computation progress
                    const pollInterval = 2000;
                    let questionCompleted = false;

                    while (!questionCompleted) {
                        await new Promise(resolve => setTimeout(resolve, pollInterval));

                        try {
                            const questionStatus = await apiClient.get<any>(
                                `/api/costs/questions/compute/status/${questionJobId}`,
                                PLAYGROUND
                            );
                            console.log(`Question cost job status:`, questionStatus);

                            // Show progress: 0-25% for question costs
                            const questionProgress = Math.min(25, (questionStatus.progress || 0) * 0.25);
                            setLoadingProgress(questionProgress);
                            setLoadingMessage(questionStatus.message || 'Computing question costs...');

                            if (questionStatus.status === 'completed') {
                                console.log('✅ Question cost computation completed');
                                questionCompleted = true;
                            } else if (questionStatus.status === 'failed') {
                                console.warn('Question cost computation failed, continuing with attempt costs...');
                                questionCompleted = true;
                            }
                        } catch (err) {
                            console.warn('Failed to fetch question cost job status');
                            questionCompleted = true;
                        }
                    }
                }
            } catch (err) {
                console.warn('Failed to start question cost computation, continuing with attempt costs...', err);
            }

            // Step 2: Start background job for attempt costs
            setLoadingMessage('Starting attempt cost computation...');
            const { jobId, message } = await apiClient.post<{ jobId: string; message: string }>(
                '/api/sessions/summary/refresh',
                PLAYGROUND
            );
            console.log(`✅ Started background job: ${jobId}`);
            setLoadingMessage(message || 'Processing attempts...');

            // Step 3: Poll for progress
            const pollInterval = 2000; // Poll every 2 seconds
            let completed = false;

            while (!completed) {
                await new Promise(resolve => setTimeout(resolve, pollInterval));

                try {
                    const status = await apiClient.get<any>(
                        `/api/sessions/summary/refresh/status/${jobId}`,
                        PLAYGROUND
                    );
                console.log(`Job status:`, status);

                // Update progress
                setLoadingProgress(status.progress || 0);
                setLoadingMessage(status.message || 'Processing...');

                // Check if job is complete
                if (status.status === 'completed') {
                    console.log('✅ Background job completed');
                    setLoadingMessage('Refresh complete! Reloading sessions...');
                    completed = true;

                    // Reload sessions via server-side pagination
                    await fetchPage(1);

                    // Also refresh question-level costs
                    setLoadingMessage('Fetching question costs...');
                    await fetchQuestionCosts();
                } else if (status.status === 'failed') {
                    throw new Error(status.message || 'Background job failed');
                }
                } catch (err) {
                    console.warn('Failed to fetch job status');
                    continue;
                }
            }

        } catch (err: any) {
            console.error('Error refreshing session summaries:', err);
            setError(err.message || 'Failed to refresh sessions');
        } finally {
            setRefreshing(false);
            setLoadingMessage('');
            setLoadingProgress(0);
        }
    }, [fetchPage, fetchQuestionCosts]);

    const backfillOrganizations = useCallback(async () => {
        setRefreshing(true);
        setLoadingMessage('Backfilling organizations and attempt types...');
        setLoadingProgress(0);

        try {
            const result = await apiClient.post<any>('/api/sessions/backfill-orgs', PLAYGROUND);
            console.log(`✅ Backfill complete:`, result);
            setLoadingMessage(`Backfill complete! ${result.updated} updated, ${result.skipped} skipped`);
            setLoadingProgress(100);

            // Reload sessions after backfill
            setTimeout(async () => {
                setLoadingMessage('Reloading sessions...');
                await fetchPage(1);
                setRefreshing(false);
                setLoadingMessage('');
                setLoadingProgress(0);
            }, 2000);

        } catch (err: any) {
            console.error('Error backfilling organizations:', err);
            setError(err.message || 'Failed to backfill organizations');
            setRefreshing(false);
            setLoadingMessage('');
            setLoadingProgress(0);
        }
    }, [fetchPage]);

    // No bulk client-side load – server provides summaries

    useEffect(() => {
        fetchSessionSummaries();
    }, [fetchSessionSummaries]);

    // Re-fetch data when environment changes (but not on initial mount)
    useEffect(() => {
        const prevEnv = prevEnvironmentRef.current;
        if (prevEnv !== undefined && prevEnv !== environment) {
            console.log('[CostAnalysis] Environment changed from', prevEnv, 'to', environment);
            console.log('[CostAnalysis] Fetching fresh data for environment:', environment);
            // Force a fresh fetch with the new environment
            fetchSessionSummaries();
            fetchOrganizations();
        }
        prevEnvironmentRef.current = environment;
    }, [environment, fetchSessionSummaries, fetchOrganizations]);

    // When filters change, re-fetch from server (page 1)
    const filterChangeRef = useRef(false);
    useEffect(() => {
        // Skip the initial mount — fetchSessionSummaries handles that
        if (!filterChangeRef.current) {
            filterChangeRef.current = true;
            return;
        }
        fetchPage(1);
    }, [dateFilter, customStartDate, customEndDate, attemptTypeFilter, orgFilter, tableFilter, debouncedSearchTerm, dataSource]); // eslint-disable-line react-hooks/exhaustive-deps

    // Old date-filter effect replaced above

    const handleSessionClick = async (session: any) => {
        const attemptId = normalizeAttemptId(session);
        if (!attemptId) {
            console.warn('No attemptId available for session', session);
            return;
        }
        // Routing matrix:
        //   - Scan-grade homework parent  → /scan-grade/homework/:id
        //   - Scan-grade orphan singleton → /scan-grade/question/:id
        //   - Canvas attempt             → /session/:id (replay)
        const path = session?.isScanGradeHomework
            ? `${BASE_PATH}/scan-grade/homework/${attemptId}`
            : session?.isScanGradeQuestion
                ? `${BASE_PATH}/scan-grade/question/${attemptId}`
                : `${BASE_PATH}/session/${attemptId}`;
        const source = session?.isScanGradeHomework
            ? 'scangrade-homework'
            : session?.isScanGradeQuestion
                ? 'scangrade-question'
                : 'canvas';
        console.log('[UI] Opening details page', { attemptId, source, url: path });
        window.open(path, '_blank');
    };

    // Helper function to calculate total cost from all individual services for a session.
    // Scan-grade rows arrive with `costs.totalCost` already pre-computed from the
    // server's rollup, so prefer that when it's a positive number — otherwise
    // fall back to summing the canvas-attempt buckets.
    const calculateSessionTotalCost = useCallback((session: any): number => {
        if (!session?.costs) return 0;
        const costs = session.costs;
        if (typeof costs.totalCost === 'number' && costs.totalCost > 0) {
            return costs.totalCost;
        }
        return (
            (costs.realtime?.totalCost || 0) +
            (costs.geminiLive?.totalCost || 0) +
            (costs.supervisor?.totalCost || costs.tutor?.totalCost || 0) +
            (costs.intentRouter?.totalCost || 0) +
            (costs.latex?.totalCost || 0) +
            (costs.questionParsing?.totalCost || 0) +
            (costs.questionValidation?.totalCost || 0) +
            (costs.conceptTagging?.totalCost || 0) +
            (costs.implicitChecker?.totalCost || 0) +
            (costs.mockTestAnalysis?.totalCost || 0) +
            (costs.fermiAnalysis?.totalCost || 0) +
            (costs.overallQuestionAnalysis?.totalCost || 0) +
            (costs.overallStudentAnalysis?.totalCost || 0) +
            (costs.scanGradeSegmentation?.totalCost || 0) +
            (costs.scanGradeGrading?.totalCost || 0)
        );
    }, []);

    const overallMetrics = useMemo(() => {
        // Use server-provided aggregates
        const realtimeCost = aggregates.realtimeCost || 0;
        const geminiLiveCost = aggregates.geminiLiveCost || 0;
        const supervisorCost = aggregates.supervisorCost || 0;
        const intentRouterCost = aggregates.intentRouterCost || 0;
        const latexCost = aggregates.latexCost || 0;
        const implicitCheckerCost = aggregates.implicitCheckerCost || 0;
        const mockTestAnalysisCost = aggregates.mockTestAnalysisCost || 0;
        const fermiAnalysisCost = aggregates.fermiAnalysisCost || 0;
        const overallQuestionAnalysisCost = aggregates.overallQuestionAnalysisCost || 0;
        const overallStudentAnalysisCost = aggregates.overallStudentAnalysisCost || 0;

        const attemptLevelTotal = realtimeCost + geminiLiveCost + supervisorCost + intentRouterCost +
            latexCost + implicitCheckerCost + mockTestAnalysisCost +
            fermiAnalysisCost + overallQuestionAnalysisCost + overallStudentAnalysisCost;

        // Question-level costs (from separate API)
        const questionLevelTotal = questionLevelCosts.questionParsing +
            questionLevelCosts.questionValidation +
            questionLevelCosts.conceptTagging +
            questionLevelCosts.curriculumTagging +
            questionLevelCosts.questionDifficulty;

        const overallCost = attemptLevelTotal + questionLevelTotal;

        const activeSessions = aggregates.activeSessions || 0;
        const totalMessages = aggregates.totalMessages || 0;

        return {
            overallCost,
            attemptLevelTotal,
            questionLevelTotal,
            realtimeCost,
            geminiLiveCost,
            supervisorCost,
            intentRouterCost,
            latexCost,
            implicitCheckerCost,
            mockTestAnalysisCost,
            fermiAnalysisCost,
            overallQuestionAnalysisCost,
            overallStudentAnalysisCost,
            activeSessions,
            avgCostPerSession: activeSessions > 0 ? attemptLevelTotal / activeSessions : 0,
            anomalyCount: aggregates.anomalyCount || 0,
            anomalyCostImpact: aggregates.anomalyCostImpact || 0,
            totalMessages,
            avgMessagesPerSession: activeSessions > 0 ? Math.round(totalMessages / activeSessions) : 0,
        };
    }, [aggregates, questionLevelCosts]);

    const dailyCostData = useMemo<DailyCostPoint[]>(() => {
        return dailyCosts.map(d => ({
            key: d.date,
            date: new Date(d.date),
            totalCost: d.totalCost,
        }));
    }, [dailyCosts]);

    const maxDailyCost = dailyCostData.reduce((max, day) => Math.max(max, day.totalCost), 0);

    // Pagination calculations — server handles filtering/sorting/pagination
    const totalRecords = totalCount;
    const totalPages = totalRecords === 0 ? 0 : Math.ceil(totalRecords / sessionsPerPage);
    const startIndex = totalRecords === 0 ? 0 : (currentPage - 1) * sessionsPerPage;
    const endIndex = startIndex + sessionsPerPage;
    const currentSessions = sessions;
    const displayStart = totalRecords === 0 ? 0 : startIndex + 1;
    const displayEnd = totalRecords === 0 ? 0 : Math.min(endIndex, totalRecords);

    const goToNextPage = () => {
        if (currentPage < totalPages) {
            fetchPage(currentPage + 1);
        }
    };

    const goToPreviousPage = () => {
        if (currentPage > 1) {
            fetchPage(currentPage - 1);
        }
    };

    const goToPage = (page: number) => {
        fetchPage(page);
    };

    if (loading) return (
        <div className="dashboard-loading">
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <h3>Loading Dashboard...</h3>
                <p>{loadingMessage}</p>
                <div style={{
                    width: '300px',
                    height: '10px',
                    backgroundColor: '#e0e0e0',
                    borderRadius: '5px',
                    margin: '20px auto',
                    overflow: 'hidden'
                }}>
                    <div style={{
                        width: `${loadingProgress}%`,
                        height: '100%',
                        backgroundColor: '#4caf50',
                        borderRadius: '5px',
                        transition: 'width 0.3s ease'
                    }}></div>
                </div>
                <small>{Math.round(loadingProgress)}% complete</small>
            </div>
        </div>
    );
    if (error) return <div className="dashboard-error">Error: {error}</div>;

    return (
        <div className="cost-dashboard">
            {/* Refresh Progress Overlay */}
            {refreshing && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '40px',
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                        minWidth: '400px',
                        textAlign: 'center'
                    }}>
                        <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>🔄 Refreshing Sessions</h3>
                        <p style={{ color: '#718096', marginBottom: '20px' }}>{loadingMessage}</p>
                        <div style={{
                            width: '100%',
                            height: '12px',
                            backgroundColor: '#e0e0e0',
                            borderRadius: '6px',
                            overflow: 'hidden',
                            marginBottom: '12px'
                        }}>
                            <div style={{
                                width: `${loadingProgress}%`,
                                height: '100%',
                                backgroundColor: '#4f46e5',
                                borderRadius: '6px',
                                transition: 'width 0.3s ease'
                            }}></div>
                        </div>
                        <small style={{ color: '#718096', fontWeight: '600' }}>
                            {Math.round(loadingProgress)}% complete
                        </small>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="dashboard-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <h1>💰 AI Session Cost Dashboard</h1>
                    <span style={{
                        padding: '4px 12px',
                        fontSize: '13px',
                        fontWeight: '600',
                        borderRadius: '5px',
                        backgroundColor: environment === 'local' ? '#fff3cd' : environment === 'staging' ? '#d1ecf1' : '#d4edda',
                        color: '#333',
                        border: '1px solid #ccc'
                    }}>
                        {environment === 'local' ? '🟡 Local' : environment === 'staging' ? '🔵 Staging' : '🟢 Production'}
                    </span>
                    {totalCount === 0 && !loading && (
                        <span style={{
                            padding: '4px 12px',
                            fontSize: '12px',
                            fontWeight: '500',
                            borderRadius: '5px',
                            backgroundColor: '#fef3c7',
                            color: '#92400e',
                            border: '1px solid #fcd34d'
                        }}>
                            ⚠️ No cached data. Click "Refresh Sessions" to compute costs.
                        </span>
                    )}
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <button
                        onClick={backfillOrganizations}
                        disabled={refreshing}
                        style={{
                            padding: '8px 12px',
                            background: refreshing ? '#94a3b8' : '#10b981',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            cursor: refreshing ? 'not-allowed' : 'pointer',
                            fontWeight: 600,
                        }}
                        title="Backfill missing organizations and attempt types for existing sessions"
                    >
                        {refreshing ? 'Backfilling…' : 'Backfill Orgs & Types'}
                    </button>
                    <button
                        onClick={refreshSessionSummaries}
                        disabled={refreshing}
                        style={{
                            padding: '8px 12px',
                            background: refreshing ? '#94a3b8' : '#4f46e5',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            cursor: refreshing ? 'not-allowed' : 'pointer',
                            fontWeight: 600,
                        }}
                        title="Recompute and update new sessions"
                    >
                        {refreshing ? 'Refreshing…' : 'Refresh Sessions'}
                    </button>
                    <div className="date-filter">
                        <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}>
                            <option>All Time</option>
                            <option>Last 24 Hours</option>
                            <option>Last 7 Days</option>
                            <option>Last 14 Days</option>
                            <option>Last 30 Days</option>
                            <option>Custom Range</option>
                        </select>
                        {dateFilter === 'Custom Range' && (
                            <>
                                <input
                                    type="date"
                                    value={customStartDate}
                                    onChange={(e) => setCustomStartDate(e.target.value)}
                                    placeholder="Start Date"
                                />
                                <input
                                    type="date"
                                    value={customEndDate}
                                    onChange={(e) => setCustomEndDate(e.target.value)}
                                    placeholder="End Date"
                                />
                            </>
                        )}
                        <div className="date-filter-info">
                            {dateFilter === 'Custom Range' && customStartDate && customEndDate ? (
                                <small>Showing sessions from {new Date(customStartDate).toLocaleDateString()} to {new Date(customEndDate).toLocaleDateString()}</small>
                            ) : dateFilter === 'All Time' ? (
                                <small>Showing all sessions</small>
                            ) : (
                                <small>Showing sessions from {dateFilter.toLowerCase()}</small>
                            )}
                        </div>
                    </div>
                    {/* Close wrapper that groups refresh button + date filter */}
                </div>
            </div>

            <div className="dashboard-content">
                {/* Filters Section */}
                <div style={{ marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {/* Data Source Toggle: canvas vs scan-and-grade */}
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, fontSize: '14px', minWidth: '150px' }}>Source:</span>
                        <button
                            className={`filter-btn ${dataSource === 'canvas' ? 'active' : ''}`}
                            onClick={() => setDataSource('canvas')}
                        >
                            Canvas Attempts
                        </button>
                        <button
                            className={`filter-btn ${dataSource === 'scangrade' ? 'active' : ''}`}
                            onClick={() => setDataSource('scangrade')}
                        >
                            Scan &amp; Grade Attempts
                        </button>
                    </div>

                    {/* Attempt Type Filters — canvas only */}
                    {dataSource === 'canvas' && (
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, fontSize: '14px', minWidth: '150px' }}>Attempt Type:</span>
                        <button
                            className={`filter-btn ${attemptTypeFilter === 'all' ? 'active' : ''}`}
                            onClick={() => setAttemptTypeFilter('all')}
                        >
                            All Attempts
                        </button>
                        <button
                            className={`filter-btn ${attemptTypeFilter === 'student' ? 'active' : ''}`}
                            onClick={() => setAttemptTypeFilter('student')}
                        >
                            Student Attempts
                        </button>
                        <button
                            className={`filter-btn ${attemptTypeFilter === 'homework' ? 'active' : ''}`}
                            onClick={() => setAttemptTypeFilter('homework')}
                        >
                            Homework Attempts
                        </button>
                        <button
                            className={`filter-btn ${attemptTypeFilter === 'mocktest' ? 'active' : ''}`}
                            onClick={() => setAttemptTypeFilter('mocktest')}
                        >
                            Mock Test Attempts
                        </button>
                    </div>
                    )}

                    {/* Organization Filter */}
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 600, fontSize: '14px', minWidth: '150px' }}>Organization:</span>
                        <div style={{ position: 'relative', minWidth: '250px' }}>
                            <input
                                type="text"
                                placeholder={orgFilter === 'all' ? 'Search organizations...' : orgFilter}
                                value={orgSearchQuery}
                                onChange={(e) => {
                                    setOrgSearchQuery(e.target.value);
                                    setOrgDropdownOpen(true);
                                }}
                                onFocus={() => setOrgDropdownOpen(true)}
                                onBlur={() => setTimeout(() => setOrgDropdownOpen(false), 200)}
                                style={{
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    border: '1px solid #d1d5db',
                                    fontSize: '14px',
                                    backgroundColor: '#fff',
                                    width: '100%',
                                    boxSizing: 'border-box',
                                }}
                            />
                            {orgDropdownOpen && (
                                <div style={{
                                    position: 'absolute',
                                    top: '100%',
                                    left: 0,
                                    right: 0,
                                    maxHeight: '240px',
                                    overflowY: 'auto',
                                    backgroundColor: '#fff',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                    zIndex: 10,
                                    marginTop: '2px',
                                }}>
                                    <div
                                        style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '14px', color: '#6b7280' }}
                                        onMouseDown={() => { setOrgFilter('all'); setOrgSearchQuery(''); setOrgDropdownOpen(false); }}
                                    >
                                        All Organizations
                                    </div>
                                    {allOrgs
                                        .filter(org => !orgSearchQuery || org.title.toLowerCase().includes(orgSearchQuery.toLowerCase()))
                                        .map(org => (
                                            <div
                                                key={org._id}
                                                style={{
                                                    padding: '8px 12px',
                                                    cursor: 'pointer',
                                                    fontSize: '14px',
                                                    backgroundColor: orgFilter === org.title ? '#eff6ff' : undefined,
                                                }}
                                                onMouseDown={() => { setOrgFilter(org.title); setOrgSearchQuery(''); setOrgDropdownOpen(false); }}
                                            >
                                                {org.title}
                                            </div>
                                        ))
                                    }
                                </div>
                            )}
                        </div>
                        {orgFilter !== 'all' && (
                            <button
                                className="filter-btn"
                                onClick={() => { setOrgFilter('all'); setOrgSearchQuery(''); }}
                                style={{ fontSize: '12px', padding: '6px 10px' }}
                            >
                                Clear
                            </button>
                        )}
                    </div>
                </div>

                {/* Overall Metrics */}
                <div>
                    <div className="metrics-grid">
                        <div className="metric-card">
                            <div className="metric-label">Overall Cost</div>
                            <div className="metric-value">{formatCost(overallMetrics.overallCost)}</div>
                        </div>
                        <div className="metric-card">
                            <div className="metric-label">Active Sessions</div>
                            <div className="metric-value">{overallMetrics.activeSessions.toLocaleString()}</div>
                            {(attemptTypeFilter !== 'all' || orgFilter !== 'all') && (
                                <div className="metric-change neutral" style={{ fontSize: '11px' }}>
                                    Filtered results
                                </div>
                            )}
                        </div>
                        <div className="metric-card">
                            <div className="metric-label">Avg Cost per Session</div>
                            <div className="metric-value">{formatCost(overallMetrics.avgCostPerSession)}</div>
                        </div>
                    </div>
                </div>

                {/* Charts Row - Split into Attempts and Questions */}
                <div className="charts-row-dual">
                    {/* Attempts Chart */}
                    <div className="chart-container">
                        <div className="chart-header">
                            <span>Attempts Costs {orgFilter !== 'all' && `(${orgFilter})`}</span>
                        </div>
                        <div className="service-breakdown">
                            {([
                                ['realtime', aggregates.realtimeCost],
                                ['geminiLive', aggregates.geminiLiveCost],
                                ['supervisor', aggregates.supervisorCost],
                                ['intentRouter', aggregates.intentRouterCost],
                                ['latex', aggregates.latexCost],
                                ['implicitChecker', aggregates.implicitCheckerCost],
                                ['fermiAnalysis', aggregates.fermiAnalysisCost],
                            ] as [string, number][]).map(([service, cost]) => (
                                <div key={service} className="service-item">
                                    <span className="service-name">{formatServiceName(service)}</span>
                                    <span className="service-cost">{formatCost(cost || 0)}</span>
                                </div>
                            ))}
                            <div className="service-item total">
                                <span className="service-name"><strong>Total</strong></span>
                                <span className="service-cost">
                                    <strong>
                                        {formatCost(
                                            (aggregates.realtimeCost || 0) + (aggregates.geminiLiveCost || 0) +
                                            (aggregates.supervisorCost || 0) +
                                            (aggregates.intentRouterCost || 0) + (aggregates.latexCost || 0) +
                                            (aggregates.implicitCheckerCost || 0) + (aggregates.fermiAnalysisCost || 0)
                                        )}
                                    </strong>
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Questions Chart */}
                    <div className="chart-container">
                        <div className="chart-header">
                            <span>Questions Costs (All Orgs)</span>
                        </div>
                        <div className="service-breakdown">
                            <div className="service-item">
                                <span className="service-name">{formatServiceName('questionParsing')}</span>
                                <span className="service-cost">{formatCost(questionLevelCosts.questionParsing)}</span>
                            </div>
                            <div className="service-item">
                                <span className="service-name">{formatServiceName('questionValidation')}</span>
                                <span className="service-cost">{formatCost(questionLevelCosts.questionValidation)}</span>
                            </div>
                            <div className="service-item">
                                <span className="service-name">{formatServiceName('conceptTagging')}</span>
                                <span className="service-cost">{formatCost(questionLevelCosts.conceptTagging)}</span>
                            </div>
                            <div className="service-item">
                                <span className="service-name">{formatServiceName('curriculumTagging')}</span>
                                <span className="service-cost">{formatCost(questionLevelCosts.curriculumTagging)}</span>
                            </div>
                            <div className="service-item">
                                <span className="service-name">{formatServiceName('questionDifficulty')}</span>
                                <span className="service-cost">{formatCost(questionLevelCosts.questionDifficulty)}</span>
                            </div>
                            <div className="service-item total">
                                <span className="service-name"><strong>Total</strong></span>
                                <span className="service-cost">
                                    <strong>
                                        {formatCost(
                                            questionLevelCosts.questionParsing +
                                            questionLevelCosts.questionValidation +
                                            questionLevelCosts.conceptTagging +
                                            questionLevelCosts.curriculumTagging +
                                            questionLevelCosts.questionDifficulty
                                        )}
                                    </strong>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Charts Row */}
                <div className="charts-row">
                    <div className="chart-container">
                        <div className="chart-header">
                            <span>Cost Trends</span>
                            <div className="chart-tabs">
                                <div className="chart-tab active">Daily</div>
                            </div>
                        </div>
                        <div className="time-series-chart" style={{ overflowX: 'auto', overflowY: 'hidden' }}>
                            {dailyCostData.length === 0 ? (
                                <div className="chart-empty">No cost data available.</div>
                            ) : (
                                <div className="bar-chart" style={{
                                    minWidth: `${Math.max(100, dailyCostData.length * 50)}px`,
                                    display: 'flex',
                                    justifyContent: dailyCostData.length > 20 ? 'flex-start' : 'space-around'
                                }}>
                                    {dailyCostData.map((day) => {
                                        const ratio = maxDailyCost > 0 ? day.totalCost / maxDailyCost : 0;
                                        const barHeight = ratio === 0 ? 0 : ratio * 100; // Scale to 0-100%);
                                        return (
                                            <div key={day.key} className="bar-column" style={{ minWidth: '45px', flex: dailyCostData.length > 20 ? '0 0 45px' : '1' }}>
                                                <div className="bar-value">{formatCost(day.totalCost)}</div>
                                                <div
                                                    className="bar"
                                                    style={{ height: `${barHeight}%` }}
                                                    title={`${day.key}: ${formatCost(day.totalCost)}`}
                                                ></div>
                                                <div className="bar-label">{dateLabelFormatter.format(day.date)}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* MongoDB Chart */}
                <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                    <iframe
                        sandbox="allow-scripts allow-same-origin"
                        style={{
                            background: '#F1F5F4',
                            border: 'none',
                            borderRadius: '2px',
                            boxShadow: '0 2px 10px 0 rgba(70, 76, 79, .2)',
                            width: '100%',
                            height: '600px'
                        }}
                        src="https://charts.mongodb.com/charts-gofermi-qrjdysm/embed/dashboards?id=68ca66ac-8371-47ea-8b36-5e381437df53&theme=light&autoRefresh=true&maxDataAge=3600&showTitleAndDesc=false&scalingWidth=fixed&scalingHeight=fixed"
                        title="MongoDB Chart"
                    ></iframe>
                </div>

                {/* P1 - Anomaly Detection - Commented out for future implementation
                <div className="priority-box p1-box">
                    <span className="priority-label p1-label">P1 - Anomaly Detection</span>
                    <div className="anomaly-section">
                        <div className="anomaly-header">
                            <h3>🚨 Recent Anomalies</h3>
                            <button className="filter-btn">View All Anomalies</button>
                        </div>
                        <div className="anomaly-alerts">
                            <div className="anomaly-alert">
                                <div className="anomaly-icon">!</div>
                                <div>
                                    <strong>High cost session detected</strong>
                                    <div>Session exceeded $2.00 threshold</div>
                                </div>
                            </div>
                            <div className="anomaly-alert">
                                <div className="anomaly-icon">⚠</div>
                                <div>
                                    <strong>Unusual token usage</strong>
                                    <div>150% above average for this hour</div>
                                </div>
                            </div>
                            <div className="anomaly-alert">
                                <div className="anomaly-icon">📈</div>
                                <div>
                                    <strong>Cost spike detected</strong>
                                    <div>30 min period with 200% increase</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                */}

                {/* P2 - Usage Heatmap - Commented out for future implementation
                <div className="priority-box p2-box">
                    <span className="priority-label p2-label">P2 - Usage Heatmap</span>
                    <div className="heatmap-container">
                        <div className="chart-header">
                            <span>Cost Heatmap by Hour (Last 7 Days)</span>
                            <div style={{display: 'flex', alignItems: 'center', gap: '15px', fontSize: '12px'}}>
                                <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                                    <div className="heat-low" style={{width: '12px', height: '12px', borderRadius: '2px'}}></div>
                                    <span>Low</span>
                                </div>
                                <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                                    <div className="heat-medium" style={{width: '12px', height: '12px', borderRadius: '2px'}}></div>
                                    <span>Medium</span>
                                </div>
                                <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                                    <div className="heat-high" style={{width: '12px', height: '12px', borderRadius: '2px'}}></div>
                                    <span>High</span>
                                </div>
                            </div>
                        </div>
                        <div className="heatmap-grid">
                            <div></div>
                            {Array.from({length: 24}, (_, i) => (
                                <div key={i} className="heatmap-label">{i.toString().padStart(2, '0')}</div>
                            ))}
                            
                            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, dayIndex) => (
                                <React.Fragment key={day}>
                                    <div className="heatmap-label" style={{fontWeight: 600}}>{day}</div>
                                    {Array.from({length: 24}, (_, hour) => {
                                        const cost = Math.random() * 5;
                                        const heatClass = cost < 1.5 ? 'heat-low' : cost < 3 ? 'heat-medium' : 'heat-high';
                                        return (
                                            <div key={hour} className={`heatmap-cell ${heatClass}`} title={`$${cost.toFixed(2)}`}>
                                                ${Math.floor(cost)}
                                            </div>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>
                */}

                {/* Session Table */}
                <div>
                    <div className="table-container">
                        <div className="table-header">
                            <h3>Session Details ({totalCount} sessions {dateFilter !== 'All Time' ? `filtered` : 'total'})</h3>
                            <div className="table-filters">
                                <input
                                    className="search-input"
                                    type="text"
                                    placeholder="Search by attemptId..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyDown={async (e) => {
                                        if (e.key === 'Enter') {
                                            const raw = searchTerm.trim();
                                            // Check if any currently-visible session matches exactly
                                            const termLower = raw.toLowerCase();
                                            const exact = sessions.find((s) => {
                                                const attemptLower = normalizeAttemptId(s).toLowerCase();
                                                const socketLower = normalizeSocketId(s).toLowerCase();
                                                return attemptLower === termLower || (socketLower !== '' && socketLower === termLower);
                                            });
                                            if (exact) {
                                                await handleSessionClick(exact);
                                            } else if (raw) {
                                                const details = await fetchSessionDetails(raw);
                                                if (!(details as any).error) {
                                                    await handleSessionClick(details);
                                                }
                                            }
                                        }
                                    }}
                                />
                                <button className="filter-btn" onClick={() => { setDebouncedSearchTerm(searchTerm); fetchPage(1); }}>Search</button>
                                <button className="filter-btn" onClick={() => { setSearchTerm(''); setDebouncedSearchTerm(''); }} disabled={!searchTerm}>Clear</button>
                                <button
                                    type="button"
                                    className={`filter-btn ${tableFilter === 'all' ? 'active' : ''}`}
                                    onClick={() => {
                                        setTableFilter('all');
                                        setCurrentPage(1);
                                    }}
                                >
                                    All Sessions
                                </button>
                                <button
                                    type="button"
                                    className={`filter-btn ${tableFilter === 'recent' ? 'active' : ''}`}
                                    onClick={() => {
                                        setTableFilter('recent');
                                        setCurrentPage(1);
                                    }}
                                >
                                    Recent
                                </button>
                            </div>
                        </div>

                        {/* Pagination Controls */}
                        <div className="pagination-controls">
                            <div className="pagination-info">
                                Showing {displayStart}-{displayEnd} of {totalRecords} sessions
                                {loadingPage && <span> (Loading...)</span>}
                            </div>
                            <div className="pagination-buttons">
                                <button
                                    className="pagination-btn"
                                    onClick={goToPreviousPage}
                                    disabled={currentPage === 1 || totalRecords === 0}
                                >
                                    ← Previous
                                </button>

                                {/* Page numbers */}
                                <div className="page-numbers">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum: number;
                                        if (totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={pageNum}
                                                className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                                                onClick={() => goToPage(pageNum)}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    className="pagination-btn"
                                    onClick={goToNextPage}
                                    disabled={totalRecords === 0 || currentPage === totalPages}
                                >
                                    Next →
                                </button>
                            </div>
                        </div>

                        <table>
                            <thead>
                                <tr>
                                    <th>Attempt ID</th>
                                    <th>Start Time</th>
                                    <th>Total Cost</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentSessions.map((session, idx) => {
                                    const attemptId = normalizeAttemptId(session);
                                    const socketId = normalizeSocketId(session);
                                    const rowKey = attemptId || socketId || `session-${idx}`;
                                    return (
                                        <tr key={rowKey} onClick={() => handleSessionClick(session)}>
                                            <td>
                                                <button
                                                    className="session-id"
                                                    onClick={(e) => { e.stopPropagation(); handleSessionClick(session); }}
                                                >
                                                    <div style={{ fontFamily: 'monospace', fontSize: 13 }}>{attemptId || '(unknown attempt)'}</div>
                                                    {socketId && socketId !== attemptId && (
                                                        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>socket: {socketId}</div>
                                                    )}
                                                </button>
                                                {calculateSessionTotalCost(session) > 1 && <span className="anomaly-badge">HIGH</span>}
                                            </td>
                                            <td>{formatStartTime(session)}</td>
                                            <td className="cost-cell">{formatCost(calculateSessionTotalCost(session))}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CostAnalysis;
